import React, { useState, useEffect, useCallback } from 'react';
import {
  Link, useNavigate, useLocation, Outlet,
} from 'react-router-dom';
import { UserDataProvider } from '../providers/UserDataProvider';
import useFetch from '../hooks/useFetch';

function InternalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(() => {
    const storedData = localStorage.getItem('userData');
    return storedData ? JSON.parse(storedData) : null;
  });
  const { fetchData, error } = useFetch();
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [marbleDayTime, setMarbleDayTime] = useState('');

  const toggleAdminMenu = () => {
    setIsAdminMenuOpen((prev) => !prev);
  };

  const handleRevertImpersonation = () => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      localStorage.setItem('jwtToken', adminToken);
      localStorage.removeItem('adminToken');
      // Navigate to a neutral admin page to force a full state refresh
      navigate('/admin/impersonate-user');
    }
  };

  const refreshUserData = useCallback(async () => {
    try {
      const freshData = await fetchData('/api/user');
      if (freshData) {
        setUserData(freshData);
        localStorage.setItem('userData', JSON.stringify(freshData));
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  }, [fetchData]);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const adminToken = localStorage.getItem('adminToken');
    setIsImpersonating(!!adminToken);

    if (!token) {
      navigate('/login');
    } else {
      refreshUserData();
    }
  }, [location, navigate, refreshUserData]);

  useEffect(() => {
    const refreshInterval = setInterval(refreshUserData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(refreshInterval);
  }, [refreshUserData]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const options = {
        timeZone: 'Etc/GMT+12',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      setMarbleDayTime(now.toLocaleString('en-US', options));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return <div>Error: {error.error}</div>;
  }

  return (
    <div className="flex bg-slate-400 h-auto min-h-screen">
      <header className="flex items-center border-b-2 border-white justify-between bg-zinc-800 text-white p-4 w-full fixed left-0 h-12 z-10 top-0">
        <div className="flex items-center w-1/3">
          <span className="text-gray-200 text-sm font-mono">
            Marble Day Time: {marbleDayTime}
          </span>
        </div>
        <div className="flex items-center justify-center w-1/3">
          {isImpersonating && (
          <div className="bg-yellow-400 text-black rounded-md px-4 py-1">
            You are currently impersonating a user.
            <button
              type="button"
              onClick={handleRevertImpersonation}
              className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
            >
              Return to Admin
            </button>
          </div>
          )}
        </div>
        <div className="flex items-center justify-end w-1/3">
          {userData ? (
            <img src={userData.profile_image_url} alt="Profile" className="h-8 w-8 rounded-full mr-2" />
          ) : (
            <div className="bg-gray-300 h-8 w-8 rounded-full mr-2">{}</div>
          )}
          <span className="text-gray-200">
            {userData ? userData.username : 'Loading...'}
          </span>
          <Link to="/logout" className="ml-4">Logout</Link>
        </div>
      </header>
      <div className="flex flex-1 mt-12">
        <aside style={{ top: '2.85rem' }} className="fixed left-0 w-64 bg-zinc-800 text-white pl-4 pr-4 pb-4 h-full overflow-y-auto border-r-2 border-white z-10">
          <img src="/assets/images/mystats_logo.png" alt="Site Logo" className="w-100" />
          <nav className="">
            <Link to="/dashboard" className="block py-2 hover:text-gray-400">Dashboard</Link>
            <Link to="/download" className="block py-2 hover:text-gray-400">Download MyStats</Link>
            <Link to="/my-events" className="block py-2 hover:text-gray-400">My Events</Link>
            <Link to="/leaderboards" className="block py-2 hover:text-gray-400">Streamer Leaderboards</Link>
            {userData && userData.isAdmin && (
              <>
                <button
                  type="button"
                  onClick={toggleAdminMenu}
                  className="block py-2 text-left w-full"
                >
                  Admin Menu
                  {' '}
                  {isAdminMenuOpen ? '▲' : '▼'}
                </button>
                {isAdminMenuOpen && (
                  <div className="pl-4">
                    <Link to="/admin/update-mystats-settings" className="block py-2 hover:text-gray-400">Update MyStats Settings</Link>
                    <Link to="/admin/upload-new-version" className="block py-2 hover:text-gray-400">Upload New Version</Link>
                    <Link to="/admin/create-patch-notes" className="block py-2 hover:text-gray-400">Create Patch Notes</Link>
                    <Link to="/admin/impersonate-user" className="block py-2 hover:text-gray-400">Impersonate User</Link>
                    <Link to="/admin/user-reports" className="block py-2 hover:text-gray-400">User Reports</Link>
                    <Link to="/admin/csv-testing" className="block py-2 hover:text-gray-400">CSV Testing</Link>
                    <Link to="/admin/season-calendar" className="block py-2 hover:text-gray-400">Season Calendar Manager</Link>
                    <a href="https://trello.com/b/E9wZxOUQ/my-stats-online" target="_blank" rel="noreferrer" className="block py-2 hover:text-gray-400">Trello Board</a>
                  </div>
                )}
              </>
            )}
          </nav>
        </aside>
        <main style={{ marginTop: '0', marginBottom: '0' }} className="flex-1 p-4 ml-64">
          <UserDataProvider userData={userData} refreshUserData={refreshUserData}>
            <Outlet />
          </UserDataProvider>
        </main>
      </div>
    </div>
  );
}

export default InternalLayout;
